import React, { useState } from 'react'
import { Activity } from "lucide-react";
import LoadingCard from './LoadingCard';
import { useQuery } from '@tanstack/react-query';
import { fetchSentimentAnalysis } from '../../api/emotionalConnect';
import PostCallFallback from "./PostCallFallback";

const hasSentimentAnalysisData = (response) => {
    const data = response?.data?.data?.data || response?.data?.data || response?.data || response || {};
    return Boolean(
        data?.overall_summary?.statement ||
        data?.sentiment_breakdown?.positive ||
        data?.sentiment_breakdown?.negative ||
        data?.self_perception_of_progress?.statement
    );
};

const SentimentAnalysis = ({
    appointmentId,
    username,
    callEndTime,
    postCallWindowExpired = false,
    finalFetchStatus = "idle",
}) => {
    const [openProof, setOpenProof] = useState({});
    const endTimestamp = callEndTime ? new Date(callEndTime).getTime() : NaN;
    const deadlineExpired =
        Boolean(postCallWindowExpired) ||
        (Number.isFinite(endTimestamp) &&
            Date.now() >= endTimestamp + 10 * 60 * 1000);

    const { data: total_data, isLoading, isFetching, refetch } = useQuery({
        queryKey: ["sentiment_analysis", appointmentId, username],
        queryFn: async ({ signal }) => {
            const response = await fetchSentimentAnalysis(
                `${username}_${appointmentId}_sentiment`,
                username,
                signal
            );
            if (!hasSentimentAnalysisData(response)) {
                throw new Error("Sentiment analysis data is not available yet.");
            }
            return response;
        },
        enabled: Boolean(username && appointmentId && !deadlineExpired),
        retry: 3,
        staleTime: Infinity,
        gcTime: Infinity,
        retryOnMount: true,
        refetchOnMount: "always",
        refetchOnWindowFocus: false,
        refetchInterval: false,
    });

    const data = total_data?.data?.data?.data || total_data?.data?.data || total_data?.data || {};
    const finalFetchPending =
        deadlineExpired && !hasSentimentAnalysisData(total_data) && finalFetchStatus === "fetching";

    const toggleProof = (key) => {
        setOpenProof((p) => ({ ...p, [key]: !p[key] }));
    };

    if (isLoading || (!deadlineExpired && isFetching) || finalFetchPending) {
        return (
            <LoadingCard message="Sentiment analysis in progress…" />
        );
    }

    if (!hasSentimentAnalysisData(total_data)) {
        return (
            <PostCallFallback
                title="Sentiment Analysis"
                icon={<Activity size={20} />}
                message={
                    deadlineExpired
                        ? "No sentiment analysis data available."
                        : "Sentiment analysis data is not available yet. You can try again."
                }
                deadlineExpired={deadlineExpired}
                onRetry={() => refetch()}
                isRetrying={isFetching}
                retryLabel="Retry Sentiment Analysis"
                retryingLabel="Retrying Sentiment Analysis…"
            />
        );
    }

    return (
        <div className="p-8 bg-white border border-gray-100 rounded-lg">
            <div className="max-w-5xl mx-auto">
                <h3 className="font-medium text-2xl text-gray-800 mb-3">Sentiment analysis</h3>
                <h4 className="text-gray-500 font-semibold mb-1">Confidence : {data.confidence?.score ?? 'n/a'}</h4>
                <div className="mt-2 text-sm text-gray-500 bg-gray-50 border-l-4 border-blue-100 p-3 rounded">{data.confidence?.proof}</div>

                <div className="divide-y divide-gray-100">
                    <section className="py-6">
                        <div className="md:flex md:items-start md:justify-between">
                            <div className="md:pr-8">
                                <h4 className="text-blue-700 font-semibold mb-2">Summary</h4>
                                <div className="text-gray-800">{data.overall_summary?.statement}</div>
                                {openProof['overall_summary'] && (
                                    <div className="mt-2 text-sm text-gray-700 bg-gray-50 border-l-4 border-blue-100 p-3 rounded">{data.overall_summary?.proof}</div>
                                )}
                            </div>
                            <div className="mt-4 md:mt-0">
                                <button onClick={() => toggleProof('overall_summary')} className="bg-blue-600 text-white rounded px-3 text-sm hover:bg-blue-700 w-32 h-8 flex items-center justify-center whitespace-nowrap">
                                    {openProof['overall_summary'] ? 'Hide conversation' : 'Show conversation'}
                                </button>
                            </div>
                        </div>
                    </section>

                    <section className="py-6">
                        <div>
                            <h4 className="text-blue-700 font-semibold mb-2">Sentiment breakdown</h4>
                            <div className="space-y-3">
                                {['positive', 'negative'].map((k) => (
                                    <div key={k}>
                                        <div className="text-gray-800 font-medium capitalize">{k} — {data.sentiment_breakdown[k]?.score}%</div>
                                        <div className="text-sm text-gray-600">{data.sentiment_breakdown[k]?.statement}</div>
                                        <div className="mt-2 text-sm text-gray-700 bg-gray-50 border-l-4 border-blue-100 p-3 rounded">{data.sentiment_breakdown[k]?.proof}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    <section className="py-6">
                        <div>
                            <h4 className="text-blue-700 font-semibold mb-2">Self perception of progress</h4>
                            <div className="text-gray-800">{data.self_perception_of_progress?.statement}</div>
                            <div className="mt-2 text-sm text-gray-700 bg-gray-50 border-l-4 border-blue-100 p-3 rounded">{data.self_perception_of_progress?.proof}</div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    )
}

export default SentimentAnalysis